import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  MapPin, ArrowRight, Truck, Trash2, PackageSearch, Thermometer, Weight, Calendar, CheckCircle2, RotateCcw,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import api from "@/lib/api";
import { tempTone, fmtDate } from "@/lib/constants";
import PostLoadDialog from "@/components/PostLoadDialog";
import PostTruckDialog from "@/components/PostTruckDialog";
import AccessGate from "@/components/AccessGate";

export default function MyPostings() {
  const [loads, setLoads] = useState([]);
  const [trucks, setTrucks] = useState([]);

  const byLiveFirst = (a, b) => {
    const al = a.live ? 1 : 0, bl = b.live ? 1 : 0;
    if (al !== bl) return bl - al; // live first
    return String(b.created_at || "").localeCompare(String(a.created_at || "")); // newest first
  };

  const fetchAll = useCallback(async () => {
    try {
      const [l, t] = await Promise.all([api.get("/loads/mine"), api.get("/trucks/mine")]);
      setLoads(l.data); setTrucks(t.data);
    } catch (e) { console.error("Failed to load postings:", e); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const del = async (type, id) => {
    try {
      await api.delete(`/${type}/${id}`);
      toast.success("Posting removed");
      fetchAll();
    } catch (e) { toast.error("Could not delete"); }
  };

  const place = async (id) => {
    try {
      await api.put(`/loads/${id}/place`);
      toast.success("Marked as placed — removed from the board");
      fetchAll();
    } catch (e) { toast.error("Could not update"); }
  };

  const renew = async (type, id) => {
    try {
      await api.put(`/${type}/${id}/renew`);
      toast.success("Reposted — back live on the board");
      fetchAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not repost"); }
  };

  const loaded = async (id) => {
    try {
      await api.put(`/trucks/${id}/loaded`);
      toast.success("Marked as loaded — removed from the board");
      fetchAll();
    } catch (e) { toast.error("Could not update"); }
  };

  const activeLoads = loads.filter((l) => l.status !== "placed").sort(byLiveFirst);
  const placedLoads = loads.filter((l) => l.status === "placed");
  const sortedTrucks = [...trucks].sort(byLiveFirst);

  return (
    <AccessGate>
      <div className="max-w-5xl">
        <h1 className="font-heading font-extrabold text-3xl tracking-tight mb-1">My Postings</h1>
        <p className="text-muted-foreground text-sm mb-6">Manage the loads and trucks you've posted.</p>

        <Tabs defaultValue="loads">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <TabsList>
              <TabsTrigger data-testid="tab-my-loads" value="loads">Loads ({activeLoads.length})</TabsTrigger>
              <TabsTrigger data-testid="tab-my-trucks" value="trucks">Trucks ({trucks.length})</TabsTrigger>
              <TabsTrigger data-testid="tab-placed" value="placed">Placed ({placedLoads.length})</TabsTrigger>
            </TabsList>
            <div className="flex gap-2">
              <PostLoadDialog onCreated={fetchAll} />
              <PostTruckDialog onCreated={fetchAll} />
            </div>
          </div>

          <TabsContent value="loads">
            {activeLoads.length === 0 ? <Empty icon={PackageSearch} text="You haven't posted any loads yet." /> : (
              <div className="grid md:grid-cols-2 gap-4">
                {activeLoads.map((l) => (
                  <div key={l.id} data-testid="my-load-card" className="rounded-xl border border-border bg-card/60 p-5">
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <div className="flex items-center gap-2 font-heading font-semibold min-w-0">
                        <MapPin className="w-4 h-4 text-primary shrink-0" /><span className="truncate">{l.origin}</span>
                        <ArrowRight className="w-4 h-4 text-muted-foreground shrink-0" /><span className="truncate">{l.destination}</span>
                      </div>
                      <button data-testid="delete-load-btn" onClick={() => del("loads", l.id)} className="text-muted-foreground hover:text-destructive transition-colors shrink-0"><Trash2 className="w-4 h-4" /></button>
                    </div>
                    <div className="mb-2 flex items-center gap-2">
                      <StatusBadge status={l.status} live={l.live} />
                      {l.status === "open" && l.live && (
                        <button data-testid="place-load-btn" onClick={() => place(l.id)} className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-300 hover:text-emerald-200 transition-colors">
                          <CheckCircle2 className="w-3.5 h-3.5" /> Mark placed
                        </button>
                      )}
                      {!l.live && (
                        <button data-testid="renew-load-btn" onClick={() => renew("loads", l.id)} className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:opacity-80 transition-colors">
                          <RotateCcw className="w-3.5 h-3.5" /> Repost
                        </button>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1"><PackageSearch className="w-3 h-3" />{l.material_type}</span>
                      <span className="flex items-center gap-1"><Weight className="w-3 h-3" />{l.weight}T</span>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border ${tempTone(l.temp_min)}`}><Thermometer className="w-3 h-3" />{l.temp_min}°/{l.temp_max}°</span>
                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{fmtDate(l.pickup_date)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="trucks">
            {sortedTrucks.length === 0 ? <Empty icon={Truck} text="You haven't posted any trucks yet." /> : (
              <div className="grid md:grid-cols-2 gap-4">
                {sortedTrucks.map((t) => (
                  <div key={t.id} data-testid="my-truck-card" className="rounded-xl border border-border bg-card/60 p-5">
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <div className="flex items-center gap-2 font-heading font-semibold min-w-0">
                        <Truck className="w-4 h-4 text-primary shrink-0" /><span className="truncate">{t.truck_type}</span>
                      </div>
                      <button data-testid="delete-truck-btn" onClick={() => del("trucks", t.id)} className="text-muted-foreground hover:text-destructive transition-colors shrink-0"><Trash2 className="w-4 h-4" /></button>
                    </div>
                    <div className="mb-2 flex items-center gap-2">
                      <StatusBadge status={t.status} live={t.live} />
                      {t.status === "open" && t.live && (
                        <button data-testid="loaded-truck-btn" onClick={() => loaded(t.id)} className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-300 hover:text-emerald-200 transition-colors">
                          <CheckCircle2 className="w-3.5 h-3.5" /> Mark as loaded
                        </button>
                      )}
                      {!t.live && (
                        <button data-testid="renew-truck-btn" onClick={() => renew("trucks", t.id)} className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:opacity-80 transition-colors">
                          <RotateCcw className="w-3.5 h-3.5" /> Repost
                        </button>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{t.current_location}</span>
                      <span className="flex items-center gap-1"><Weight className="w-3 h-3" />{t.capacity}T</span>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border ${tempTone(t.temp_min)}`}><Thermometer className="w-3 h-3" />{t.temp_min}°/{t.temp_max}°</span>
                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{fmtDate(t.available_date)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="placed">
            {placedLoads.length === 0 ? <Empty icon={CheckCircle2} text="No placed loads yet. Tap 'Mark placed' on a load once a vehicle is assigned." /> : (
              <div className="grid md:grid-cols-2 gap-4">
                {placedLoads.map((l) => (
                  <div key={l.id} data-testid="placed-load-card" className="rounded-xl border border-border bg-card/60 p-5">
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <div className="flex items-center gap-2 font-heading font-semibold min-w-0">
                        <MapPin className="w-4 h-4 text-primary shrink-0" /><span className="truncate">{l.origin}</span>
                        <ArrowRight className="w-4 h-4 text-muted-foreground shrink-0" /><span className="truncate">{l.destination}</span>
                      </div>
                      <StatusBadge status={l.status} live={l.live} />
                    </div>
                    <div className="flex flex-wrap gap-3 text-xs text-muted-foreground mb-3">
                      <span className="flex items-center gap-1"><PackageSearch className="w-3 h-3" />{l.material_type}</span>
                      <span className="flex items-center gap-1"><Weight className="w-3 h-3" />{l.weight}T</span>
                      {l.placed_at && <span className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />Placed {fmtDate(l.placed_at)}</span>}
                    </div>
                    <button data-testid="renew-placed-btn" onClick={() => renew("loads", l.id)} className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:opacity-80 transition-colors">
                      <RotateCcw className="w-3.5 h-3.5" /> Repost this load
                    </button>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </AccessGate>
  );
}

function StatusBadge({ status, live }) {
  if (status === "placed" || status === "loaded") return <span data-testid="status-badge" className="px-2 py-0.5 rounded-full text-xs border bg-primary/15 text-primary border-primary/30">{status === "loaded" ? "Loaded" : "Placed"}</span>;
  if (live) return <span data-testid="status-badge" className="px-2 py-0.5 rounded-full text-xs border bg-emerald-500/15 text-emerald-300 border-emerald-500/30">Live</span>;
  return <span data-testid="status-badge" className="px-2 py-0.5 rounded-full text-xs border bg-secondary text-muted-foreground border-border">Expired</span>;
}

function Empty({ icon: Icon, text }) {
  return (
    <div className="text-center py-16 border border-dashed border-border rounded-xl">
      <Icon className="w-10 h-10 text-muted-foreground/50 mx-auto mb-3" />
      <p className="text-muted-foreground">{text}</p>
    </div>
  );
}
